import {
  Badge,
  Box,
  Button,
  Divider,
  Flex,
  HStack,
  IconButton,
  Input,
  Textarea,
  VStack,
} from '@chakra-ui/react';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { Plus, Trash2 } from 'lucide-react';
import { useRef, useState } from 'react';
import { iconSize, pxToRem } from '@duke-hq/ui/constants/scalable';

import { testIds } from './testIds';
import { GroupHeading, OutlineHeading, OutlineItem, ReviewLayout } from './ReviewLayout';
import { UnitRequirementKindHeading } from '../../../components/requirements/kinds';
import {
  belongsToElement,
  nextCriterionCode,
  nextElementCode,
  nextEvidenceCode,
} from '@duke-hq/unit-generation-pipeline/requirements/requirementCodes';
import {
  blank,
  type EvidenceItem,
  type RequirementSet,
  type RequirementSetIssues,
} from '../reviewedRequirements';

type UnitElement = RequirementSet['elements'][number];
type PerformanceCriterion = RequirementSet['performanceCriteria'][number];

function criteriaOf(
  set: RequirementSet,
  elementCode: string,
): Array<{ criterion: PerformanceCriterion; index: number }> {
  return set.performanceCriteria
    .map((criterion, index) => ({ criterion, index }))
    .filter(({ criterion }) => belongsToElement(criterion.code, elementCode));
}

type NavSelection =
  | { kind: 'element'; code: string }
  | { kind: 'knowledgeEvidence' }
  | { kind: 'performanceEvidence' }
  | { kind: 'foundationSkills' }
  | { kind: 'assessmentConditions' };

type ActivePane =
  | { kind: 'element'; element: UnitElement }
  | Exclude<NavSelection, { kind: 'element' }>;

function CodeBadge({ code, isInvalid }: { code: string; isInvalid?: boolean }) {
  return (
    <Badge
      colorScheme={isInvalid ? 'red' : 'blue'}
      fontFamily="mono"
      fontSize="xs"
      flexShrink={0}
      minW={pxToRem(56)}
      textAlign="center"
    >
      {code}
    </Badge>
  );
}

function EvidenceGroup({
  items,
  prefix,
  label,
  addLabel,
  duplicateCodes,
  onChange,
}: {
  items: EvidenceItem[];
  prefix: 'KE' | 'PE';
  label: React.ReactNode;
  addLabel: React.ReactNode;
  duplicateCodes: Set<string>;
  onChange: (items: EvidenceItem[]) => void;
}) {
  return (
    <VStack align="stretch" spacing={2}>
      <GroupHeading>{label}</GroupHeading>

      {items.map((item, index) => (
        <HStack key={item.code} spacing={2}>
          <CodeBadge code={item.code} isInvalid={duplicateCodes.has(item.code)} />
          <Input
            size="sm"
            value={item.description}
            isInvalid={blank(item.description)}
            aria-label={t`Description for ${item.code}`}
            onChange={(event) =>
              onChange(
                items.map((current, at) =>
                  at === index ? { ...current, description: event.target.value } : current,
                ),
              )
            }
          />
          <IconButton
            size="sm"
            variant="ghost"
            colorScheme="red"
            aria-label={t`Delete ${item.code}`}
            icon={<Trash2 size={iconSize.sm} />}
            onClick={() => onChange(items.filter((_, at) => at !== index))}
          />
        </HStack>
      ))}

      <Button
        size="xs"
        variant="outline"
        alignSelf="flex-start"
        leftIcon={<Plus size={iconSize.xs} />}
        onClick={() =>
          onChange([...items, { code: nextEvidenceCode(items, prefix), description: '' }])
        }
      >
        {addLabel}
      </Button>
    </VStack>
  );
}

export function UnitRequirementsStep({
  value,
  issues,
  hidePerformanceEvidence = false,
  onChange,
}: {
  value: RequirementSet;
  issues: RequirementSetIssues;
  hidePerformanceEvidence?: boolean;
  onChange: (next: RequirementSet) => void;
}) {
  const defaultSelection: NavSelection =
    value.elements[0] == null
      ? { kind: 'knowledgeEvidence' }
      : { kind: 'element', code: value.elements[0].code };

  const [selected, setSelected] = useState<NavSelection>(defaultSelection);
  const skillRowKeys = useRef<string[]>([]);

  const skillRows = value.foundationSkillsStructured.map((skill, index) => {
    const key = skillRowKeys.current[index] ?? crypto.randomUUID();
    skillRowKeys.current[index] = key;

    return { skill, index, key };
  });

  let activePane: ActivePane;

  if (selected.kind !== 'element') {
    activePane = selected;
  } else {
    const element = value.elements.find(({ code }) => code === selected.code) ?? value.elements[0];

    activePane = element == null ? { kind: 'knowledgeEvidence' } : { kind: 'element', element };
  }

  const patch = (next: Partial<RequirementSet>) => onChange({ ...value, ...next });

  const patchSkill =
    (index: number, field: 'category' | 'description') =>
    (event: React.ChangeEvent<HTMLInputElement>) =>
      patch({
        foundationSkillsStructured: value.foundationSkillsStructured.map((current, at) =>
          at === index ? { ...current, [field]: event.target.value } : current,
        ),
      });

  const addElement = () => {
    const code = nextElementCode(value.elements);

    patch({ elements: [...value.elements, { code, title: '' }] });
    setSelected({ kind: 'element', code });
  };

  const deleteElement = (code: string) =>
    patch({
      elements: value.elements.filter((element) => element.code !== code),
      performanceCriteria: value.performanceCriteria.filter(
        (criterion) => !belongsToElement(criterion.code, code),
      ),
    });

  const addCriterion = (elementCode: string) =>
    patch({
      performanceCriteria: [
        ...value.performanceCriteria,
        {
          code: nextCriterionCode(value.performanceCriteria, elementCode),
          element: Number(elementCode),
          description: '',
        },
      ],
    });

  const criteriaByElement = new Map(
    value.elements.map((element) => [element.code, criteriaOf(value, element.code)]),
  );

  const rowIssue = (code: string, description: string) =>
    blank(description) || issues.duplicateCodes.has(code);

  const elementIssue = (element: UnitElement) =>
    blank(element.title) ||
    issues.duplicateCodes.has(element.code) ||
    (criteriaByElement.get(element.code) ?? []).some(({ criterion }) =>
      rowIssue(criterion.code, criterion.description),
    );

  const evidenceIssue = (items: EvidenceItem[]) =>
    items.some(({ code, description }) => rowIssue(code, description));

  const foundationSkillsIssue = value.foundationSkillsStructured.some(
    ({ category, description }) =>
      blank(category) || blank(description) || issues.duplicateCategories.has(category),
  );

  return (
    <ReviewLayout
      outline={
        <>
          <OutlineHeading>
            <Trans>Elements</Trans>
          </OutlineHeading>

          {value.elements.map((element) => (
            <OutlineItem
              key={element.code}
              isSelected={activePane.kind === 'element' && activePane.element.code === element.code}
              hasIssue={elementIssue(element)}
              badge={
                <Badge colorScheme="blue" fontFamily="mono" fontSize="xs" flexShrink={0}>
                  {element.code}
                </Badge>
              }
              count={(criteriaByElement.get(element.code) ?? []).length}
              label={blank(element.title) ? t`Untitled element` : element.title}
              onSelect={() => setSelected({ kind: 'element', code: element.code })}
            />
          ))}

          <Button
            size="xs"
            variant="ghost"
            alignSelf="flex-start"
            leftIcon={<Plus size={iconSize.xs} />}
            data-testid={testIds.addElementButton}
            onClick={addElement}
          >
            <Trans>Add element</Trans>
          </Button>

          <Divider my={2} />

          <OutlineItem
            isSelected={activePane.kind === 'knowledgeEvidence'}
            hasIssue={evidenceIssue(value.knowledgeEvidence)}
            count={value.knowledgeEvidence.length}
            label={<UnitRequirementKindHeading kind="KNOWLEDGE_EVIDENCE" />}
            onSelect={() => setSelected({ kind: 'knowledgeEvidence' })}
          />
          {hidePerformanceEvidence ? null : (
            <OutlineItem
              isSelected={activePane.kind === 'performanceEvidence'}
              hasIssue={evidenceIssue(value.performanceEvidence)}
              count={value.performanceEvidence.length}
              label={<UnitRequirementKindHeading kind="PERFORMANCE_EVIDENCE" />}
              onSelect={() => setSelected({ kind: 'performanceEvidence' })}
            />
          )}
          <OutlineItem
            isSelected={activePane.kind === 'foundationSkills'}
            hasIssue={foundationSkillsIssue}
            count={value.foundationSkillsStructured.length}
            label={<UnitRequirementKindHeading kind="FOUNDATION_SKILL" />}
            onSelect={() => setSelected({ kind: 'foundationSkills' })}
          />
          <OutlineItem
            isSelected={activePane.kind === 'assessmentConditions'}
            hasIssue={false}
            label={<Trans>Assessment conditions</Trans>}
            onSelect={() => setSelected({ kind: 'assessmentConditions' })}
          />
        </>
      }
    >
      {activePane.kind === 'element' ? (
        <VStack align="stretch" spacing={5}>
          <Box>
            <Box mb={2}>
              <GroupHeading>
                <Trans>Element</Trans>
              </GroupHeading>
            </Box>

            <HStack spacing={2}>
              <CodeBadge
                code={activePane.element.code}
                isInvalid={issues.duplicateCodes.has(activePane.element.code)}
              />
              <Input
                size="sm"
                fontWeight="medium"
                placeholder={t`Element title`}
                value={activePane.element.title}
                isInvalid={blank(activePane.element.title)}
                aria-label={t`Title for element ${activePane.element.code}`}
                onChange={(event) =>
                  patch({
                    elements: value.elements.map((current) =>
                      current.code === activePane.element.code
                        ? { ...current, title: event.target.value }
                        : current,
                    ),
                  })
                }
              />
              <IconButton
                size="sm"
                variant="ghost"
                colorScheme="red"
                data-testid={testIds.deleteElementButton}
                aria-label={t`Delete element ${activePane.element.code}`}
                icon={<Trash2 size={iconSize.sm} />}
                onClick={() => deleteElement(activePane.element.code)}
              />
            </HStack>
          </Box>

          <Divider />

          <Box>
            <Box mb={2}>
              <GroupHeading>
                <UnitRequirementKindHeading kind="PERFORMANCE_CRITERION" />
              </GroupHeading>
            </Box>
            <VStack align="stretch" spacing={2}>
              {(criteriaByElement.get(activePane.element.code) ?? []).map(
                ({ criterion, index }) => (
                  <HStack key={criterion.code} spacing={2}>
                    <CodeBadge
                      code={criterion.code}
                      isInvalid={issues.duplicateCodes.has(criterion.code)}
                    />
                    <Input
                      size="sm"
                      value={criterion.description}
                      isInvalid={blank(criterion.description)}
                      placeholder={t`Performance criterion`}
                      aria-label={t`Description for ${criterion.code}`}
                      onChange={(event) =>
                        patch({
                          performanceCriteria: value.performanceCriteria.map((current, at) =>
                            at === index
                              ? { ...current, description: event.target.value }
                              : current,
                          ),
                        })
                      }
                    />
                    <IconButton
                      size="sm"
                      variant="ghost"
                      colorScheme="red"
                      aria-label={t`Delete ${criterion.code}`}
                      icon={<Trash2 size={iconSize.sm} />}
                      onClick={() =>
                        patch({
                          performanceCriteria: value.performanceCriteria.filter(
                            (_, at) => at !== index,
                          ),
                        })
                      }
                    />
                  </HStack>
                ),
              )}
              <Button
                size="xs"
                variant="ghost"
                alignSelf="flex-start"
                leftIcon={<Plus size={iconSize.xs} />}
                data-testid={testIds.addCriterionButton}
                onClick={() => addCriterion(activePane.element.code)}
              >
                <Trans>Add criterion</Trans>
              </Button>
            </VStack>
          </Box>
        </VStack>
      ) : activePane.kind === 'knowledgeEvidence' ? (
        <EvidenceGroup
          items={value.knowledgeEvidence}
          prefix="KE"
          label={<UnitRequirementKindHeading kind="KNOWLEDGE_EVIDENCE" />}
          addLabel={<Trans>Add knowledge evidence</Trans>}
          duplicateCodes={issues.duplicateCodes}
          onChange={(knowledgeEvidence) => patch({ knowledgeEvidence })}
        />
      ) : activePane.kind === 'performanceEvidence' ? (
        <EvidenceGroup
          items={value.performanceEvidence}
          prefix="PE"
          label={<UnitRequirementKindHeading kind="PERFORMANCE_EVIDENCE" />}
          addLabel={<Trans>Add performance evidence</Trans>}
          duplicateCodes={issues.duplicateCodes}
          onChange={(performanceEvidence) => patch({ performanceEvidence })}
        />
      ) : activePane.kind === 'foundationSkills' ? (
        <VStack align="stretch" spacing={2}>
          <GroupHeading>
            <UnitRequirementKindHeading kind="FOUNDATION_SKILL" />
          </GroupHeading>

          {skillRows.map(({ skill, index, key }) => (
            <Flex key={key} gap={2}>
              <Box flexShrink={0} w={pxToRem(160)}>
                <Input
                  size="sm"
                  value={skill.category}
                  maxLength={32}
                  placeholder={t`Category`}
                  isInvalid={
                    blank(skill.category) || issues.duplicateCategories.has(skill.category)
                  }
                  aria-label={t`Foundation skill category`}
                  onChange={patchSkill(index, 'category')}
                />
              </Box>

              <Input
                size="sm"
                value={skill.description}
                isInvalid={blank(skill.description)}
                placeholder={t`How the skill is used`}
                aria-label={t`Foundation skill description`}
                onChange={patchSkill(index, 'description')}
              />

              <IconButton
                size="sm"
                variant="ghost"
                colorScheme="red"
                aria-label={t`Delete foundation skill`}
                icon={<Trash2 size={iconSize.sm} />}
                onClick={() => {
                  skillRowKeys.current.splice(index, 1);
                  patch({
                    foundationSkillsStructured: value.foundationSkillsStructured.filter(
                      (_, at) => at !== index,
                    ),
                  });
                }}
              />
            </Flex>
          ))}

          <Button
            size="xs"
            variant="outline"
            alignSelf="flex-start"
            leftIcon={<Plus size={iconSize.xs} />}
            onClick={() =>
              patch({
                foundationSkillsStructured: [
                  ...value.foundationSkillsStructured,
                  { category: '', description: '' },
                ],
              })
            }
          >
            <Trans>Add foundation skill</Trans>
          </Button>
        </VStack>
      ) : (
        <VStack align="stretch" spacing={2} flex={1}>
          <GroupHeading>
            <Trans>Assessment conditions</Trans>
          </GroupHeading>
          <Textarea
            size="sm"
            flex={1}
            minH={pxToRem(200)}
            resize="none"
            value={value.assessmentConditions}
            aria-label={t`Assessment conditions`}
            onChange={(event) => patch({ assessmentConditions: event.target.value })}
          />
        </VStack>
      )}
    </ReviewLayout>
  );
}
